import { motion } from "framer-motion";
import { Check, ArrowUpRight, Lock } from "lucide-react";
import { scrollToSection } from "../lib/site";

const plans = [
  {
    name: "The Roadmap",
    price: "₹30,000",
    cadence: "one-time",
    tagline: "A crash course in strategy — which doors to knock, and which to kick.",
    features: [
      "Deep-dive strategy sessions for your brand & category",
      "Platform playbook — Amazon, Shopify or quick commerce",
      "Which doors to knock, and which to kick",
      "Pricing, margins & unit-economics guidance",
      "Step-by-step launch checklist & roadmap",
      "Pure guidance — you execute, we point the way",
    ],
    popular: false,
    cta: "Get the Roadmap",
  },
  {
    name: "Guided Launch",
    price: "₹25,000",
    cadence: "/month · 3 months",
    tagline: "Hand-holding through launch — a joint responsibility.",
    features: [
      "Everything in The Roadmap",
      "Joint launch on 1 marketplace or quick commerce platform",
      "We work alongside your team, every step",
      "Catalog, listings & content reviewed by us",
      "Weekly working sessions until you're live",
      "3-month structured engagement",
    ],
    popular: true,
    cta: "Launch Together",
  },
  {
    name: "Full Accountability",
    price: "₹5,00,000",
    cadence: "/year",
    tagline: "We own the outcome, not just the advice.",
    features: [
      "Everything in Guided Launch",
      "End-to-end accountability for launch & scale",
      "eCommerce or quick commerce — we run it with you",
      "Monthly fee structure tied to your launch",
      "Ads, growth & performance management",
      "Yearly contract & committed partnership",
    ],
    popular: false,
    cta: "Partner With Us",
  },
];

export const Pricing = ({ unlocked }) => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 lg:py-40 bg-midnight"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {!unlocked ? (
          <motion.div
            data-testid="pricing-locked"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="border border-coal/15 px-8 py-20 lg:py-28 text-center max-w-3xl mx-auto"
          >
            <Lock size={34} strokeWidth={1.5} className="mx-auto text-gold" />
            <span className="block mt-6 text-xs font-bold uppercase tracking-[0.25em] text-coal/40">
              06 — Pricing
            </span>
            <h2 className="font-display font-900 tracking-tight leading-none text-3xl sm:text-4xl lg:text-5xl mt-4">
              Clear scope.{" "}
              <span className="font-accent italic font-medium text-terracotta">
                Clear price.
              </span>
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-coal/60 max-w-xl mx-auto">
              Three ways to work with us — from pure strategy to full
              accountability. The packages and pricing unlock after a verified
              enquiry.
            </p>
            <button
              data-testid="pricing-unlock-cta"
              onClick={() => scrollToSection("#contact")}
              className="mt-10 inline-flex items-center gap-2.5 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 hover:bg-gold transition-colors duration-300"
            >
              Send an Enquiry to Unlock
              <ArrowUpRight size={17} strokeWidth={2.5} />
            </button>
          </motion.div>
        ) : (
        <>
        <div className="flex items-start justify-between mb-16 lg:mb-24">
          <div>
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-coal/40"
            >
              06 — Pricing
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-900 tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl"
            >
              Clear scope.{" "}
              <span className="font-accent italic font-medium text-terracotta">
                Clear price.
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-coal/60"
            >
              Three ways to work with us — from pure strategy to full
              accountability. Pick your depth, or start with a free audit and
              we'll tell you which one actually fits.
            </motion.p>
          </div>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block font-display font-900 text-8xl text-outline-gold select-none"
          >
            06
          </motion.span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-coal/10 border border-coal/10">
          {plans.map((plan, i) => (
            <motion.article
              key={plan.name}
              data-testid={`pricing-card-${i}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.8,
                delay: i * 0.12,
                ease: [0.16, 1, 0.3, 1],
              }}
              whileHover={{ y: -4 }}
              className={`group relative flex flex-col p-8 lg:p-12 transition-colors duration-500 ${
                plan.popular
                  ? "bg-ink hover:bg-steel"
                  : "bg-midnight hover:bg-ink"
              }`}
            >
              <div
                className={`absolute top-0 left-0 h-[3px] transition-[width] duration-500 ${
                  plan.popular
                    ? "w-full bg-terracotta"
                    : "w-0 bg-terracotta group-hover:w-full"
                }`}
              />
              {plan.popular && (
                <span
                  data-testid="pricing-popular-badge"
                  className="absolute top-6 right-6 text-[11px] font-bold uppercase tracking-[0.2em] text-night bg-terracotta px-3 py-1.5"
                >
                  Most Popular
                </span>
              )}
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
                {plan.name}
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display font-900 tracking-tighter text-5xl sm:text-6xl">
                  {plan.price}
                </span>
                <span className="text-sm text-coal/45">{plan.cadence}</span>
              </div>
              <p className="mt-4 font-accent italic text-lg text-coal/70">
                {plan.tagline}
              </p>
              <ul className="mt-10 space-y-4 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-coal/65">
                    <Check
                      size={16}
                      strokeWidth={2.5}
                      className="text-terracotta shrink-0 mt-0.5"
                    />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-cta-${i}`}
                onClick={() => scrollToSection("#contact")}
                className={`mt-12 inline-flex items-center justify-center gap-2.5 font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 transition-colors duration-300 ${
                  plan.popular
                    ? "bg-terracotta text-night hover:bg-gold"
                    : "border border-coal/25 text-coal hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {plan.cta}
                <ArrowUpRight size={17} strokeWidth={2.5} />
              </button>
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          data-testid="pricing-note"
          className="mt-10 text-xs uppercase tracking-[0.2em] text-coal/35 text-center"
        >
          All engagements begin with a free audit · Government fees, marketplace charges & GST not included · Custom scopes on request
        </motion.p>
        </>
        )}
      </div>
    </section>
  );
};
